import _            from 'lodash';
import Immutable    from 'immutable';
import { delegate } from 'alto-utils/lib/macros';

class Association {
  constructor(name, backendName, sourceModel, destinationModel, foreignKey) {
    this.name             = name;
    this.backendName      = backendName;
    this.sourceModel      = sourceModel;
    this.destinationModel = destinationModel;
    this.foreignKey       = foreignKey;
  }

  toString() {
    return (
      `${this.constructor.name} { name: ${this.name}, sourceModel: ${this.sourceModel.name}, ` +
      `destinationModel: ${this.destinationModel.name}, foreignKey: ${this.foreignKey} }`
    );
  }
}

export class BelongsTo extends Association {}
export class HasMany   extends Association {}
export class HasOne    extends Association {}

export class HasManyThrough {
  constructor(name, sourceModel, assocNames) {
    this.name        = name;
    this.sourceModel = sourceModel;
    this.assocNames  = assocNames;
  }
}

export class AssociationCollection {
  static initClass() {
    delegate(this, 'map', 'forEach', 'find', {to: 'associations'});
  }

  constructor(associations = []) {
    this.associations = new Immutable.Set(associations);
  }

  add(association) {
    return new this.constructor(this.associations.add(association));
  }

  merge(other) {
    return new this.constructor(this.associations.union(other.associations));
  }

  findBy(sourceModel, name) {
    return this.associations.find(assoc => assoc.name === name && assoc.sourceModel === sourceModel)
  }

  forModel(sourceModel) {
    return new this.constructor(
      this.associations.filter(assoc => assoc.sourceModel === sourceModel)
    );
  }

  ofType(type) {
    return new this.constructor(this.associations.filter(assoc => assoc instanceof type));
  }

  backendPathFor(sourceModel, path) {
    const chain      = this._chainForPath(sourceModel, path);
    const names      = _.map(chain, 'backendName');
    const separators = path.match(/\.|\|/g);

    return _.compact(_.flatten(_.zip(names, separators))).join('');
  }

  finalModelFor(sourceModel, path) {
    return _.last(this._chainForPath(sourceModel, path)).destinationModel;
  }

  destinationModelsForPaths(sourceModel, paths) {
    return _.uniq(_.map(this._uniqueForPaths(sourceModel, paths), 'destinationModel'));
  }

  _uniqueForPaths(sourceModel, paths) {
    return _.uniq(_.concat(...this._chainsForPaths(sourceModel, paths)));
  }

  _chainsForPaths(sourceModel, paths) {
    return paths.map(path => this._chainForPath(sourceModel, path));
  }

  _chainForPath(sourceModel, path) {
    const associations = [];

    for (let name of _.split(path, /\.|\|/)) {
      const association = this.findBy(sourceModel, name);

      if (!association) { throw Error(`association for ${sourceModel.name}.${name} not found`); }

      associations.push(association);
      sourceModel = association.destinationModel;
    }

    return associations;
  }
};

AssociationCollection.initClass();
